import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { LeadFormFields } from "@/components/applications/LeadFormFields";
import { PageHeader } from "@/components/layout/PageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { emptyLeadForm, parseLeadForm } from "@/lib/lead-form";
import { ApiError } from "@/lib/api/client";
import { createApplication } from "@/services/applications";
import type { LeadFormPayload } from "@/types/application";

export function CreateApplicationPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<LeadFormPayload>(emptyLeadForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const setField = <K extends keyof LeadFormPayload>(key: K, value: LeadFormPayload[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = parseLeadForm(form);
    if (!parsed.success) {
      setFieldErrors(parsed.fieldErrors);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      const created = await createApplication(parsed.data);
      navigate(`/applications/${created.uuid}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const duplicatePhone =
          /phone number/i.test(err.message) && /already exists/i.test(err.message);
        setError(
          duplicatePhone ? "A user with this phone number already exists" : err.message,
        );
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
      } else {
        setError("Unable to create lead");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="New lead"
        title="Create lead"
        description="Add the business owner details to create a lead. You’ll be taken to the application to upload documents."
      />

      <form
        onSubmit={onSubmit}
        className="max-w-3xl space-y-6 rounded-2xl border border-line bg-surface-raised p-5 sm:p-6"
        noValidate
      >
        {error ? <Alert>{error}</Alert> : null}

        <LeadFormFields form={form} fieldErrors={fieldErrors} onChange={setField} />

        <div className="flex items-center justify-end gap-2 border-t border-line pt-5">
          <Link to="/applications">
            <Button type="button" variant="ghost">
              Back
            </Button>
          </Link>
          <Button type="submit" loading={loading}>
            Create lead
          </Button>
        </div>
      </form>
    </div>
  );
}
